import React, { useState, useEffect } from 'react';
import { Tabs, Button, Dropdown, Avatar, Modal, Switch, message } from 'antd';
import type { MenuProps } from 'antd';
import { QrcodeOutlined, ShoppingOutlined, FileTextOutlined, DollarOutlined, SettingOutlined, ArrowLeftOutlined, UserOutlined, LogoutOutlined, HistoryOutlined, PushpinOutlined, PushpinFilled, BellOutlined } from '@ant-design/icons';
import TicketUse from './components/TicketUse';
import TicketSale from './components/TicketSale';
import TicketList from './components/TicketList';
import TicketTypeManagement from './components/TicketTypeManagement';
import RevenueStatistics from './components/RevenueStatistics';
import TransactionLog from './components/TransactionLog';
import NotificationModal from './popup/NotificationModal';
import { useHistory } from 'react-router';
import { useDispatch } from 'react-redux';
import { logout } from 'redux/authSlice';
import { _AUTH } from 'constants/_auth';
import settingService from 'services/setting.service';
import { PrintSettingsProvider, usePrintSettings } from 'contexts/PrintSettingsContext';

const { TabPane } = Tabs;

const TicketManagementContent: React.FC = () => {
    let history = useHistory();
    const dispatch = useDispatch();
    const [activeTab, setActiveTab] = useState('use');
    const [activeRightTab, setActiveRightTab] = useState('sale');
    const [selectedStudentForSale, setSelectedStudentForSale] = useState<any>(null);
    const [autoUseForSale, setAutoUseForSale] = useState(false);
    const [isPinned, setIsPinned] = useState(() => {
        const saved = localStorage.getItem('ticketPagePinned');
        return saved === 'true';
    });
    const [isSettingsModalVisible, setIsSettingsModalVisible] = useState(false);
    const [isNotificationModalVisible, setIsNotificationModalVisible] = useState(false);
    const [printSettings, setPrintSettings] = useState<any[]>([]);
    const [loading, setLoading] = useState(false);
    const { refreshPrintSettings, updatePrintSettings } = usePrintSettings();

    // Check authentication - redirect to login if not authenticated
    useEffect(() => {
        const token = localStorage.getItem(_AUTH.TOKEN_NAME);
        if (!token) {
            message.warning('Vui lòng đăng nhập để tiếp tục');
            history.push('/auth');
            return;
        }

        // Check role authorization
        const userRole = localStorage.getItem(_AUTH.ROLE) || '';
        const allowedRoles = ['Admin', 'ServiceManager', 'CanTeenStaff', 'ParkingStaff', 'DormitoryStaff'];

        if (!allowedRoles.includes(userRole)) {
            message.error('Bạn không có quyền truy cập trang dịch vụ');
            history.push('/');
            return;
        }
    }, [history]);

    // Get user info from localStorage
    const username = localStorage.getItem(_AUTH.USERNAME) || 'User';
    const role = localStorage.getItem(_AUTH.ROLE) || '';
    const isServiceStaff = role === 'CanTeenStaff' || role === 'ParkingStaff' || role === 'DormitoryStaff';

    // Load print settings on component mount
    useEffect(() => {
        const loadPrintSettings = async () => {
            try {
                const response = await settingService.getSettingPrint();
                if (response.result) {
                    setPrintSettings(response.result);
                }
            } catch (error) {
                console.error('Failed to load print settings:', error);
            }
        };

        loadPrintSettings();
    }, []);

    // Save isPinned to localStorage whenever it changes
    useEffect(() => {
        localStorage.setItem('ticketPagePinned', isPinned.toString());
    }, [isPinned]);

    const getRoleName = (role: string) => {
        switch (role) {
            case 'Admin':
                return 'Quản trị viên';
            case 'ServiceStaff':
                return 'Nhân viên dịch vụ';
            case 'ServiceManager':
                return 'Quản lý dịch vụ';
            default:
                return role;
        }
    };

    const handleLogout = () => {
        dispatch(logout());
    };

    const togglePin = () => {
        setIsPinned(!isPinned);
    };

    const showSettingsModal = async () => {
        setIsSettingsModalVisible(true);
        setLoading(true);
        try {
            const response: any = await settingService.getSettingPrint();
            if (response?.result) {
                setPrintSettings(response.result);
            }
        } catch (error) {
            message.error('Không thể tải cài đặt');
        } finally {
            setLoading(false);
        }
    };

    const handleSettingsOk = async () => {
        setLoading(true);
        try {
            await settingService.settingPrint(printSettings);
            // Cập nhật context sau khi lưu thành công
            updatePrintSettings(printSettings);
            message.success('Lưu cài đặt thành công');
            setIsSettingsModalVisible(false);
        } catch (error) {
            message.error('Không thể lưu cài đặt');
        } finally {
            setLoading(false);
        }
    };

    const handleSettingsCancel = () => {
        setIsSettingsModalVisible(false);
    };

    const handleSwitchChange = (key: string, checked: boolean) => {
        setPrintSettings(prev =>
            prev.map(setting =>
                setting.key === key
                    ? { ...setting, value: checked ? "1" : "0" }
                    : setting
            )
        );
    };

    const getSettingLabel = (key: string) => {
        switch (key) {
            case 'AllowPrintTicketWhenSell':
                return 'Cho phép in phiếu khi bán';
            case 'AllowPrintTicketWhenUse':
                return 'Cho phép in phiếu khi sử dụng';
            default:
                return key;
        }
    };

    const userMenu: MenuProps = {
        items: [
            {
                key: 'info',
                disabled: true,
                label: (
                    <div className="px-2 py-1">
                        <p className="font-semibold text-gray-800">{username}</p>
                        <p className="text-sm text-gray-500">{getRoleName(role)}</p>
                    </div>
                )
            },
            {
                type: 'divider'
            },
            {
                key: 'logout',
                icon: <LogoutOutlined />,
                label: 'Đăng xuất',
                danger: true,
                onClick: handleLogout
            }
        ]
    };

    // Render for ServiceStaff (mobile-like layout)
    if (isServiceStaff) {
        return (
            <div className="p-2 sm:p-4 bg-gray-50 min-h-screen">
                <div className="bg-white rounded-lg shadow-md p-3 sm:p-6">
                    {/* Header with back button and user info */}
                    <div className="flex justify-between items-center mb-2">
                        <Button
                            type="text"
                            icon={<ArrowLeftOutlined />}
                            onClick={() => history.goBack()}
                            className="flex items-center gap-1 sm:gap-2 text-xs sm:text-sm"
                        >
                            <span className="hidden xs:inline">Quay lại</span>
                        </Button>

                        <Dropdown menu={userMenu} placement="bottomRight" trigger={['click']}>
                            <div className="flex items-center gap-1 sm:gap-2 cursor-pointer hover:bg-gray-50 px-2 sm:px-3 py-1 sm:py-2 rounded-lg transition-colors">
                                <Avatar size="small" icon={<UserOutlined />} className="bg-blue-500 sm:w-8 sm:h-8" />
                                <span className="font-medium text-gray-700 text-xs sm:text-sm truncate max-w-[80px] sm:max-w-none">{username}</span>
                            </div>
                        </Dropdown>
                    </div>

                    <Tabs
                        activeKey={activeTab}
                        onChange={setActiveTab}
                        size="small"
                        type="card"
                        className="ticket-responsive-tabs"
                    >
                        <TabPane
                            tab={
                                <span className="flex items-center gap-1 sm:gap-2 px-2 sm:px-4">
                                    <QrcodeOutlined className="text-base sm:text-xl" />
                                    <span className="font-semibold text-xs sm:text-sm">Sử dụng</span>
                                </span>
                            }
                            key="use"
                        >
                            <TicketUse onSwitchToSale={(studentInfo) => {
                                setActiveTab('sale');
                                if (studentInfo) {
                                    setSelectedStudentForSale(studentInfo);
                                    setAutoUseForSale(true);
                                }
                            }} />
                        </TabPane>
                    </Tabs>
                </div>
            </div>
        );
    }

    // Render for Admin and ServiceManager (split layout on desktop)
    return (
        <div className="p-2 sm:p-4 bg-gray-50 min-h-screen">
            <div className="bg-white rounded-lg shadow-md p-3 sm:p-6">
                {/* Header with back button and user info */}
                <div className="flex justify-between items-center mb-2 flex-wrap gap-2">
                    <div className="flex items-center gap-1 sm:gap-2 flex-wrap">
                        <Button
                            type="text"
                            icon={<ArrowLeftOutlined />}
                            onClick={() => history.goBack()}
                            className="flex items-center gap-1 sm:gap-2 text-xs sm:text-sm px-1 sm:px-3"
                            size="small"
                        >
                            <span className="hidden sm:inline">Quay lại</span>
                        </Button>
                        
                        {/* Pin/Unpin button - Only show on desktop */}
                        <Button
                            type={isPinned ? "primary" : "default"}
                            icon={isPinned ? <PushpinFilled /> : <PushpinOutlined />}
                            onClick={togglePin}
                            className="hidden lg:flex items-center gap-2"
                        >
                            {isPinned ? 'Tắt ghim' : 'Ghim'}
                        </Button>

                        {/* Settings button */}
                        <Button
                            icon={<SettingOutlined />}
                            onClick={showSettingsModal}
                            className="flex items-center gap-1 sm:gap-2 focus:outline-none focus:shadow-none active:shadow-none text-xs sm:text-sm"
                        >
                            <span className="hidden sm:inline">Thiết lập</span>
                        </Button>

                        {/* Notification button */}
                        <Button
                            icon={<BellOutlined />}
                            onClick={() => setIsNotificationModalVisible(true)}
                            className="flex items-center gap-1 sm:gap-2 focus:outline-none focus:shadow-none active:shadow-none text-xs sm:text-sm"
                        >
                            <span className="hidden sm:inline">Thông báo</span>
                        </Button>
                    </div>

                    <Dropdown menu={userMenu} placement="bottomRight" trigger={['click']}>
                        <div className="flex items-center gap-1 sm:gap-2 cursor-pointer hover:bg-gray-50 px-2 sm:px-3 py-1 sm:py-2 rounded-lg transition-colors">
                            <Avatar size="small" icon={<UserOutlined />} className="bg-blue-500 sm:w-8 sm:h-8" />
                            <span className="font-medium text-gray-700 text-xs sm:text-sm truncate max-w-[80px] sm:max-w-none">{username}</span>
                        </div>
                    </Dropdown>
                </div>

                {/* Settings Modal */}
                <Modal
                    title={
                        <div className="flex items-center gap-2">
                            <SettingOutlined className="text-blue-600" />
                            <span>Thiết lập in phiếu</span>
                        </div>
                    }
                    open={isSettingsModalVisible}
                    onOk={handleSettingsOk}
                    onCancel={handleSettingsCancel}
                    okText="Lưu"
                    cancelText="Hủy"
                    confirmLoading={loading}
                >
                    <div className="space-y-4 py-4">
                        {loading && printSettings.length === 0 ? (
                            <div className="text-center py-4">Đang tải...</div>
                        ) : (
                            printSettings.map((setting) => (
                                <div key={setting.key} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                                    <span className="font-medium">{getSettingLabel(setting.key)}</span>
                                    <Switch
                                        className="focus:outline-none focus:shadow-none active:shadow-none"
                                        checked={setting.value === "1"}
                                        onChange={(checked) => handleSwitchChange(setting.key, checked)}
                                    />
                                </div>
                            ))
                        )}
                    </div>
                </Modal>

                {/* Mobile view - tabs as before */}
                <div className="block lg:hidden">
                    <Tabs
                        activeKey={activeTab}
                        onChange={setActiveTab}
                        size="small"
                        type="card"
                        className="ticket-responsive-tabs"
                        tabBarStyle={{ marginBottom: 8 }}
                    >
                        <TabPane
                            tab={
                                <span className="flex items-center gap-1 px-1 sm:px-2">
                                    <QrcodeOutlined className="text-base sm:text-lg" />
                                    <span className="font-semibold text-xs sm:text-sm hidden xs:inline">Sử dụng</span>
                                </span>
                            }
                            key="use"
                        >
                            <TicketUse isPinned={false} onSwitchToSale={(studentInfo) => {
                                setActiveTab('sale');
                                setSelectedStudentForSale(studentInfo || null);
                                if (studentInfo) {
                                    setAutoUseForSale(true);
                                }
                            }} />
                        </TabPane>

                        <TabPane
                            tab={
                                <span className="flex items-center gap-1 px-1 sm:px-2">
                                    <ShoppingOutlined className="text-base sm:text-lg" />
                                    <span className="font-semibold text-xs sm:text-sm hidden xs:inline">Bán</span>
                                </span>
                            }
                            key="sale"
                        >
                            <TicketSale
                                initialStudent={selectedStudentForSale}
                                autoUseOnLoad={autoUseForSale}
                                onStudentUsed={() => {
                                    setSelectedStudentForSale(null);
                                    setAutoUseForSale(false);
                                }}
                            />
                        </TabPane>

                        <TabPane
                            tab={
                                <span className="flex items-center gap-1 px-1 sm:px-2">
                                    <FileTextOutlined className="text-base sm:text-lg" />
                                    <span className="font-semibold text-xs sm:text-sm hidden xs:inline">DS</span>
                                </span>
                            }
                            key="list"
                        >
                            <TicketList />
                        </TabPane>

                        <TabPane
                            tab={
                                <span className="flex items-center gap-1 px-1 sm:px-2">
                                    <DollarOutlined className="text-base sm:text-lg" />
                                    <span className="font-semibold text-xs sm:text-sm hidden xs:inline">TK</span>
                                </span>
                            }
                            key="revenue"
                        >
                            <RevenueStatistics />
                        </TabPane>

                        <TabPane
                            tab={
                                <span className="flex items-center gap-1 px-1 sm:px-2">
                                    <SettingOutlined className="text-base sm:text-lg" />
                                    <span className="font-semibold text-xs sm:text-sm hidden xs:inline">Loại</span>
                                </span>
                            }
                            key="type"
                        >
                            <TicketTypeManagement />
                        </TabPane>

                        <TabPane
                            tab={
                                <span className="flex items-center gap-1 px-1 sm:px-2">
                                    <HistoryOutlined className="text-base sm:text-lg" />
                                    <span className="font-semibold text-xs sm:text-sm hidden xs:inline">Log</span>
                                </span>
                            }
                            key="log"
                        >
                            <TransactionLog />
                        </TabPane>
                    </Tabs>
                </div>

                {/* Desktop view - Conditional layout based on isPinned */}
                {isPinned ? (
                    // Pinned: split layout - col-5 left (Use tab) + col-7 right (other tabs)
                    <div className="hidden lg:grid lg:grid-cols-12 gap-4" style={{ height: 'calc(100vh - 140px)' }}>
                        {/* Left panel - col-5 - Ticket Use (always visible) */}
                        <div className="col-span-5 border-r pr-4 overflow-y-auto">
                            <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 mb-4 sticky top-0 z-10">
                                <div className="flex items-center gap-2">
                                    <QrcodeOutlined className="text-xl text-blue-600" />
                                    <span className="font-semibold text-blue-800">Sử dụng phiếu</span>
                                </div>
                            </div>
                            <TicketUse onSwitchToSale={(studentInfo) => {
                                setActiveRightTab('sale');
                                setSelectedStudentForSale(studentInfo || null);
                                if (studentInfo) {
                                    setAutoUseForSale(true);
                                }
                            }} />
                        </div>

                        {/* Right panel - col-7 - Other tabs */}
                        <div className="col-span-7 overflow-y-auto">
                            <Tabs
                                activeKey={activeRightTab}
                                onChange={setActiveRightTab}
                                size="large"
                                type="card"
                            >
                                <TabPane
                                    tab={
                                        <span className="flex items-center gap-2 px-4">
                                            <ShoppingOutlined className="text-xl" />
                                            <span className="font-semibold">Bán phiếu</span>
                                        </span>
                                    }
                                    key="sale"
                                >
                                    <TicketSale
                                        initialStudent={selectedStudentForSale}
                                        autoUseOnLoad={autoUseForSale}
                                        onStudentUsed={() => {
                                            setSelectedStudentForSale(null);
                                            setAutoUseForSale(false);
                                        }}
                                    />
                                </TabPane>

                                <TabPane
                                    tab={
                                        <span className="flex items-center gap-2 px-4">
                                            <FileTextOutlined className="text-xl" />
                                            <span className="font-semibold">Danh sách phiếu</span>
                                        </span>
                                    }
                                    key="list"
                                >
                                    <TicketList />
                                </TabPane>

                                <TabPane
                                    tab={
                                        <span className="flex items-center gap-2 px-4">
                                            <DollarOutlined className="text-xl" />
                                            <span className="font-semibold">Thống kê</span>
                                        </span>
                                    }
                                    key="revenue"
                                >
                                    <RevenueStatistics />
                                </TabPane>

                                <TabPane
                                    tab={
                                        <span className="flex items-center gap-2 px-4">
                                            <SettingOutlined className="text-xl" />
                                            <span className="font-semibold">Quản lý loại phiếu</span>
                                        </span>
                                    }
                                    key="type"
                                >
                                    <TicketTypeManagement />
                                </TabPane>

                                <TabPane
                                    tab={
                                        <span className="flex items-center gap-2 px-4">
                                            <HistoryOutlined className="text-xl" />
                                            <span className="font-semibold">Nhật ký</span>
                                        </span>
                                    }
                                    key="log"
                                >
                                    <TransactionLog />
                                </TabPane>
                            </Tabs>
                        </div>
                    </div>
                ) : (
                    // Unpinned: normal tabs layout (full screen for each tab)
                    <div className="hidden lg:block">
                        <Tabs
                            activeKey={activeTab}
                            onChange={setActiveTab}
                            size="large"
                            type="card"
                        >
                            <TabPane
                                tab={
                                    <span className="flex items-center gap-2 px-4">
                                        <QrcodeOutlined className="text-xl" />
                                        <span className="font-semibold">Sử dụng</span>
                                    </span>
                                }
                                key="use"
                            >
                                <TicketUse isPinned={false} onSwitchToSale={(studentInfo) => {
                                    setActiveTab('sale');
                                    setSelectedStudentForSale(studentInfo || null);
                                    if (studentInfo) {
                                        setAutoUseForSale(true);
                                    }
                                }} />
                            </TabPane>

                            <TabPane
                                tab={
                                    <span className="flex items-center gap-2 px-4">
                                        <ShoppingOutlined className="text-xl" />
                                        <span className="font-semibold">Bán phiếu</span>
                                    </span>
                                }
                                key="sale"
                            >
                                <TicketSale
                                    initialStudent={selectedStudentForSale}
                                    autoUseOnLoad={autoUseForSale}
                                    onStudentUsed={() => {
                                        setSelectedStudentForSale(null);
                                        setAutoUseForSale(false);
                                    }}
                                />
                            </TabPane>

                            <TabPane
                                tab={
                                    <span className="flex items-center gap-2 px-4">
                                        <FileTextOutlined className="text-xl" />
                                        <span className="font-semibold">Danh sách phiếu</span>
                                    </span>
                                }
                                key="list"
                            >
                                <TicketList />
                            </TabPane>

                            <TabPane
                                tab={
                                    <span className="flex items-center gap-2 px-4">
                                        <DollarOutlined className="text-xl" />
                                        <span className="font-semibold">Thống kê</span>
                                    </span>
                                }
                                key="revenue"
                            >
                                <RevenueStatistics />
                            </TabPane>

                            <TabPane
                                tab={
                                    <span className="flex items-center gap-2 px-4">
                                        <SettingOutlined className="text-xl" />
                                        <span className="font-semibold">Quản lý loại phiếu</span>
                                    </span>
                                }
                                key="type"
                            >
                                <TicketTypeManagement />
                            </TabPane>

                            <TabPane
                                tab={
                                    <span className="flex items-center gap-2 px-4">
                                        <HistoryOutlined className="text-xl" />
                                        <span className="font-semibold">Nhật ký</span>
                                    </span>
                                }
                                key="log"
                            >
                                <TransactionLog />
                            </TabPane>
                        </Tabs>
                    </div>
                )}

                {/* Notification Modal */}
                <NotificationModal
                    visible={isNotificationModalVisible}
                    onClose={() => setIsNotificationModalVisible(false)}
                    onSuccess={() => {
                        message.success('Thông báo đã được gửi thành công');
                    }}
                />
            </div>
        </div>
    );
};

const TicketManagement: React.FC = () => {
    return (
        <PrintSettingsProvider>
            <TicketManagementContent />
        </PrintSettingsProvider>
    );
};

export default TicketManagement;
